import * as THREE from 'three';
import type { Clip, Rig } from './asset-contract';
import type { SocialPlayback } from './interaction-playback';

export function clipTime(clip: Clip, elapsed: number): number {
  if (elapsed < 0) return 0;
  if (clip.loop.enabled && elapsed > clip.loop.out) return clip.loop.in + (elapsed - clip.loop.out) % (clip.loop.out - clip.loop.in);
  return Math.min(elapsed, clip.duration);
}
export class BentoPlayback {
  private bones = new Map<string, THREE.Bone>();
  private a = new THREE.Quaternion();
  private b = new THREE.Quaternion();
  private lastPose: { position: THREE.Vector3; rotation: THREE.Quaternion }[];
  private exitPose: { position: THREE.Vector3; rotation: THREE.Quaternion }[];
  private socialKey = '';
  private blendOut = .15;
  private exitTime = 0;
  private exitDuration = 0;
  private base: Clip | string = '';
  constructor(private rig: Rig, root: THREE.Object3D) {
    root.traverse(node => { if (node instanceof THREE.Bone) this.bones.set(node.name, node); });
    if (rig.joints.some(j => !this.bones.has(j.name))) throw new Error('Avatar skeleton is incomplete');
    const pose = () => rig.joints.map(j => ({ position: this.bones.get(j.name)!.position.clone(), rotation: this.bones.get(j.name)!.quaternion.clone() }));
    this.lastPose = pose(); this.exitPose = pose();
  }
  bone(name: string): THREE.Bone | undefined { return this.bones.get(name); }
  update(clip: Clip | null, elapsed: number, moving: boolean, social: SocialPlayback | null = null, dt = .05): void {
    const key = social ? social.key ?? social.kind : '', base = moving ? 'walk' : clip ?? 'idle';
    // Two bounded layers: locomotion/idle, then one masked gesture or interaction.
    // Shared starts use authoritative elapsed time. Cancellation blends the last
    // displayed pose back to base without retaining an asset or moving the root.
    if (!social && (this.socialKey || (this.base && this.base !== base))) {
      this.exitDuration = this.socialKey ? this.blendOut : .15; this.exitTime = 0;
      this.lastPose.forEach((p, index) => { this.exitPose[index]!.position.copy(p.position); this.exitPose[index]!.rotation.copy(p.rotation); });
    }
    if (social) { this.exitDuration = 0; this.blendOut = social.blendOut ?? .15; }
    this.socialKey = key; this.base = base;
    for (const joint of this.rig.joints) { const bone = this.bones.get(joint.name)!; bone.position.fromArray(joint.translation); bone.quaternion.identity(); bone.scale.setScalar(1); }
    const gait = moving && (!social || social.gesture) ? Math.sin(elapsed * Math.PI * 2) * .45 : 0;
    this.bones.get('mShoulderLeft')!.rotation.set(-gait * .7, 0, -1.15); this.bones.get('mShoulderRight')!.rotation.set(gait * .7, 0, 1.15);
    this.bones.get('mHipLeft')!.rotation.x = gait; this.bones.get('mHipRight')!.rotation.x = -gait;
    // Small client-only idle breathing. Authored playback owns its poses; this
    // cosmetic base motion is suppressed while any authored clip/action plays.
    if (!moving && !clip && !social) this.bones.get('mChest')!.position.y += .004 * Math.sin(elapsed * Math.PI / 2);
    if (clip && !moving && (!social || social.gesture)) this.applyClip(clip, elapsed, 1);
    if (social) {
      const blend = social.blendIn ?? .15, weight = social.elapsed <= 0 ? 0 : blend === 0 ? 1 : Math.min(1, social.elapsed / blend);
      if (social.kind === 'authored') { if (social.clip) this.applyClip(social.clip, social.elapsed, weight); }
      else if (social.kind === 'sit') {
        const pelvis = this.bones.get('mPelvis')!;
        pelvis.position.y = THREE.MathUtils.lerp(pelvis.position.y, social.seatHeight + .05, weight);
        for (const side of ['Left', 'Right']) {
          this.bones.get(`mHip${side}`)!.rotation.x = -Math.PI / 2 * weight;
          this.bones.get(`mKnee${side}`)!.rotation.x = Math.PI / 2 * weight;
        }
      } else {
        this.bones.get('mShoulderLeft')!.rotation.z = THREE.MathUtils.lerp(-1.15, .35, weight);
        this.bones.get('mElbowLeft')!.rotation.x = (-1.2 + .35 * Math.sin(social.elapsed * Math.PI * 2)) * weight;
      }
    }
    this.exitTime += Math.max(0, Math.min(dt, .1));
    const exitWeight = this.exitDuration > 0 ? Math.max(0, 1-this.exitTime/this.exitDuration) : 0;
    this.rig.joints.forEach((joint, index) => {
      const bone = this.bones.get(joint.name)!, last = this.lastPose[index]!;
      if (exitWeight > 0) { bone.position.lerp(this.exitPose[index]!.position, exitWeight); bone.quaternion.slerp(this.exitPose[index]!.rotation, exitWeight); }
      last.position.copy(bone.position); last.rotation.copy(bone.quaternion);
    });
  }
  private applyClip(clip: Clip, elapsed: number, weight: number): void {
    const time = clipTime(clip, elapsed);
    for (const track of clip.tracks) {
      const bone = this.bones.get(track.joint); if (!bone) continue;
      let lo = 0, hi = track.keys.length - 1;
      while (lo + 1 < hi) { const mid = (lo + hi) >>> 1; if (track.keys[mid]!.time <= time) lo = mid; else hi = mid; }
      const before = track.keys[lo]!, after = track.keys[hi]!, amount = THREE.MathUtils.clamp((time - before.time) / (after.time - before.time), 0, 1);
      this.a.fromArray(before.rotation); this.b.fromArray(after.rotation); this.a.slerp(this.b, amount); bone.quaternion.slerp(this.a, weight);
      const rest = this.rig.joints.find(j => j.name === track.joint)!;
      for (let axis = 0; axis < 3; axis++) bone.position.setComponent(axis, THREE.MathUtils.lerp(bone.position.getComponent(axis), rest.translation[axis]! + THREE.MathUtils.lerp(before.position[axis]!, after.position[axis]!, amount), weight));
    }
  }
  diagnostics() { return { mElbowLeft: this.bones.get('mElbowLeft')!.quaternion.toArray(), mPelvis: this.bones.get('mPelvis')!.position.toArray() }; }
}
